import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'antd-mobile';

import useLineLiff from 'hooks/LineLiff';
import request from 'pages/toyotaPremiumOfficialAccount/utils/request';
import OwnMessage from '../../../components/toyotaPremium/OwnMessage';
import OtherMessage from '../../../components/toyotaPremium/OtherMessage';
import Modal from '../../../components/toyotaPremium/Modal';

const POLL_INTERVAL_MS = 3000;
const HELLO_SWAP_MS = 500;

const salesHelloText = (salesName) =>
  `我是 TOYOTA PREMIUM 專屬顧問 ${salesName}。很榮幸為您服務，請問今日想諮詢哪一台車款或服務？`;

const getClientConversation = async () => {
  const response = await request.get('/conversations');
  return response.data.data?.conversation || null;
};

const getSalesConversation = async (conversationId) => {
  const response = await request.get('/conversations_sales', {
    params: { id: conversationId },
  });
  return response.data.data?.[0] || null;
};

const getMessages = async (conversationId, afterId = 0, silent = false) => {
  const response = await request.get(
    `/conversations/${conversationId}/messages`,
    {
      params: { after_id: afterId },
      silent,
    }
  );
  return response.data.data || [];
};

const sendConversationMessage = async (conversationId, message) => {
  const response = await request.post(
    `/conversations/${conversationId}/message`,
    { message }
  );
  return response.data.data;
};

const changeConversationSales = async (conversationId, force = false) => {
  const response = await request.post(
    `/conversations/${conversationId}/change`,
    null,
    { params: { force } }
  );
  return response.data.data;
};

const resizeTextarea = (textarea) => {
  if (!textarea) return;
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const showLoadingToast = (content) =>
  Toast.show({
    content,
    icon: 'loading',
    duration: 0,
    maskClickable: false,
    maskStyle: { '--z-index': 10000 },
  });

function Chatroom({ role }) {
  const isUser = role === 'user';
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams();
  const { userInfo } = useLineLiff(process.env.REACT_APP_LIFF_ID_CONSULTATION);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesReady, setMessagesReady] = useState(false);
  const [hello, setHello] = useState(null);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [rematchStep, setRematchStep] = useState(null);
  const [isRematching, setIsRematching] = useState(false);

  const textareaRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const isSendingRef = useRef(false);
  const helloRef = useRef(null);
  const helloTimerRef = useRef(null);
  const assignHelloRef = useRef(Boolean(location.state?.showHello));

  const counterpartName = isUser
    ? conversation?.sales?.name || ''
    : conversation?.client?.name || '';
  const officeName = conversation?.sales_office?.name || '';
  const rematchSalesName = conversation?.sales?.name || '';
  const isChangingRematch = rematchStep === 'changing';
  const isSuccessRematch = rematchStep === 'success';
  const currentSalesId = conversation?.sales?.id;
  const hasCurrentSalesReply = Boolean(
    currentSalesId &&
      messages.some((message) => message?.author?.id === currentSalesId)
  );
  const showReminder = isUser && !hasCurrentSalesReply;
  const showHello = Boolean(hello?.visible && hello.salesName);
  const helloText = showHello ? salesHelloText(hello.salesName) : '';

  const fallbackPath = isUser
    ? '/ToyotaPremium/consultation/branch-list'
    : '/ToyotaPremium/consultation/conversation-list';

  useEffect(() => {
    setConversation(null);
    setMessages([]);
    setMessagesReady(false);
    helloRef.current = null;
    setHello(null);
    if (helloTimerRef.current) {
      clearTimeout(helloTimerRef.current);
      helloTimerRef.current = null;
    }
    setInput('');
    setRematchStep(null);
    lastMessageIdRef.current = 0;
  }, [conversationId]);

  useEffect(() => {
    lastMessageIdRef.current = messages.reduce(
      (max, message) => (message.id > max ? message.id : max),
      0
    );
  }, [messages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages, hello]);

  useEffect(() => {
    if (!userInfo || !conversationId || messagesReady) return undefined;
    const handler = showLoadingToast('載入中，請稍候');
    return () => handler.close();
  }, [conversationId, messagesReady, userInfo]);

  useEffect(() => {
    if (!userInfo || !conversationId) return undefined;
    let cancelled = false;

    const loadConversation = async () => {
      try {
        const loaded = isUser
          ? await getClientConversation()
          : await getSalesConversation(conversationId);
        if (cancelled) return;
        if (!loaded || Number(loaded.id) !== Number(conversationId)) {
          navigate(fallbackPath);
          return;
        }
        if (isUser && loaded.status === 'changing') {
          navigate('/ToyotaPremium/consultation/branch-list');
          return;
        }
        setConversation(loaded);
      } catch {
        if (!cancelled) {
          navigate(fallbackPath);
        }
      }
    };

    loadConversation();
    return () => {
      cancelled = true;
    };
  }, [conversationId, fallbackPath, isUser, navigate, userInfo]);

  useEffect(() => {
    if (
      !userInfo ||
      !conversationId ||
      Number(conversation?.id) !== Number(conversationId)
    ) {
      return undefined;
    }
    let cancelled = false;
    let intervalId;

    const fetchMessages = async (afterId, silent) => {
      try {
        const incoming = await getMessages(conversationId, afterId, silent);
        if (cancelled) return;
        setMessages((prev) => {
          if (afterId === 0) {
            return incoming;
          }
          const existingIds = new Set(prev.map((message) => message.id));
          const next = incoming.filter(
            (message) => message && !existingIds.has(message.id)
          );
          if (!next.length) return prev;
          return [...prev, ...next];
        });
      } catch {
        // toast already shown unless silent
      } finally {
        if (!cancelled && afterId === 0) {
          setMessagesReady(true);
        }
      }
    };

    fetchMessages(0, false).then(() => {
      if (cancelled) return;
      intervalId = setInterval(() => {
        fetchMessages(lastMessageIdRef.current, true);
      }, POLL_INTERVAL_MS);
    });

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [conversation?.id, conversationId, userInfo]);

  const presentHello = (salesName) => {
    if (!salesName) return;
    const anchorId = lastMessageIdRef.current;
    if (helloTimerRef.current) {
      clearTimeout(helloTimerRef.current);
      helloTimerRef.current = null;
    }
    if (!helloRef.current?.visible) {
      const next = { salesName, anchorId, visible: true };
      helloRef.current = next;
      setHello(next);
      return;
    }
    const hidden = { ...helloRef.current, visible: false };
    helloRef.current = hidden;
    setHello(hidden);
    helloTimerRef.current = setTimeout(() => {
      const next = { salesName, anchorId, visible: true };
      helloRef.current = next;
      setHello(next);
      helloTimerRef.current = null;
    }, HELLO_SWAP_MS);
  };

  useEffect(
    () => () => {
      if (helloTimerRef.current) clearTimeout(helloTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (
      !isUser ||
      !assignHelloRef.current ||
      !messagesReady ||
      !conversation?.sales?.name
    ) {
      return;
    }
    assignHelloRef.current = false;
    presentHello(conversation.sales.name);
  }, [conversation, isUser, messagesReady]);

  const handleInput = (e) => {
    setInput(e.target.value);
    resizeTextarea(e.target);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSendingRef.current || !conversationId) return;
    isSendingRef.current = true;
    setIsSending(true);
    try {
      const saved = await sendConversationMessage(conversationId, text);
      if (saved) {
        setMessages((prev) => {
          if (prev.some((message) => message.id === saved.id)) return prev;
          return [...prev, saved];
        });
      }
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch {
      // toast already shown in request.js
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenModal = () => {
    const isLastSales =
      conversation?.status === 'changing' ||
      conversation?.has_other_sales === false;
    setRematchStep(isLastSales ? 'changing' : 'confirm');
  };

  const handleCloseModal = () => {
    setRematchStep(null);
  };

  const handleConfirmRematch = async () => {
    if (isRematching || !conversationId) return;
    setIsRematching(true);
    const loading = showLoadingToast('配對中，請稍候');
    try {
      const updated = await changeConversationSales(conversationId, false);
      loading.close();
      if (updated?.status === 'active') {
        setConversation(updated);
        presentHello(updated.sales?.name);
        setRematchStep('success');
      }
    } catch (error) {
      // request.js already replaced this toast with the error toast; closing
      // it here would hide that message (antd-mobile Toast is a singleton).
      if (error.response?.data?.error === 'NO_OTHER_SALES') {
        setRematchStep('changing');
      }
    } finally {
      setIsRematching(false);
    }
  };

  const handleForceRematch = async () => {
    if (isRematching || !conversationId) return;
    if (conversation?.status === 'changing') {
      navigate('/ToyotaPremium/consultation/branch-list');
      return;
    }
    setIsRematching(true);
    const loading = showLoadingToast('處理中，請稍候');
    try {
      await changeConversationSales(conversationId, true);
      loading.close();
      navigate('/ToyotaPremium/consultation/branch-list');
    } catch {
      // request.js already replaced this toast with the error toast
    } finally {
      setIsRematching(false);
    }
  };

  return (
    <div className="chat-room">
      <div className="chat-header">
        <div className="chat-header__profile">
          <div className="chat-header__avatar">
            <div className="chat-header__avatar-img"></div>
          </div>
          <div className="chat-header__user-info">
            <div className="chat-header__user-name">{counterpartName}</div>
            <div className="chat-header__user-description">{officeName}</div>
          </div>
        </div>
        {isUser && (
          <button
            className="chat-header__rematch-btn"
            type="button"
            onClick={handleOpenModal}>
            重新配對
          </button>
        )}
      </div>
      {showReminder && (
        <div className="chat-reminder">
          <p className="chat-reminder__text">
            感謝您的諮詢，我們將儘速安排專屬銷售顧問於上班時間與您聯繫，敬請稍候。
          </p>
        </div>
      )}
      <div className="chat-area">
        <div className="chat-messages-bg"></div>
        <div className="chat-messages-container" ref={messagesContainerRef}>
          <div className="chat-messages">
            {showHello && hello.anchorId === 0 && (
              <OtherMessage message={helloText} />
            )}
            {messages.map((message) => {
              const isOwn =
                String(message?.author?.id) === String(userInfo?.userId);
              const MessageBubble = isOwn ? OwnMessage : OtherMessage;
              return (
                <Fragment key={message.id}>
                  <MessageBubble message={message.message} />
                  {showHello && hello.anchorId === message.id && (
                    <OtherMessage message={helloText} />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
      <div className="chat-input">
        <div className="chat-input__container">
          <textarea
            ref={textareaRef}
            className="chat-input__textarea"
            placeholder="Aa"
            rows={1}
            value={input}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
          />
        </div>
        <button
          className="chat-input__send-btn"
          type="button"
          aria-label="送出訊息"
          disabled={isSending || !input.trim()}
          onClick={handleSend}>
          <svg
            className="chat-input__send-btn-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none">
            <path
              d="M8.2851 13.845L13.7936 11.8574L17.6736 10.4324L24.7956 7.84479C24.9297 7.79647 25.0747 7.78728 25.2138 7.8183C25.3529 7.84933 25.4802 7.91927 25.581 8.01997C25.6817 8.12067 25.7517 8.24795 25.7828 8.38693C25.8138 8.52592 25.8046 8.67087 25.7563 8.80482L21.7487 19.8001L19.7374 25.3053C19.6859 25.4479 19.5923 25.5715 19.469 25.6598C19.3458 25.7482 19.1986 25.7972 19.047 25.8003C18.8967 25.8004 18.7498 25.7555 18.6253 25.6712C18.5009 25.587 18.4046 25.4673 18.349 25.3278L15.9197 19.2653C15.8273 19.0347 15.8814 18.7712 16.0571 18.5955L18.8425 15.8119C19.085 15.5695 19.085 15.1764 18.8425 14.934L18.6632 14.7548C18.4205 14.5122 18.027 14.5127 17.7848 14.7557L15.0209 17.5299C14.8455 17.7059 14.582 17.7603 14.3512 17.6682L8.2851 15.2475C8.14271 15.1935 8.02013 15.0975 7.93362 14.9723C7.84711 14.847 7.80078 14.6984 7.80078 14.5462C7.80078 14.394 7.84711 14.2454 7.93362 14.1202C8.02013 13.9949 8.14271 13.8989 8.2851 13.845Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      {isUser && (
        <Modal isOpen={Boolean(rematchStep)} onClose={handleCloseModal}>
          <div className="rematch-modal">
            <div className="rematch-modal__body">
              <h2 className="rematch-modal__title">
                {isSuccessRematch ? '重新配對成功' : '是否重新選擇專屬顧問？'}
              </h2>
              <div className="rematch-modal__description-container">
                <p
                  className={`rematch-modal__description ${
                    isChangingRematch ? 'rematch-modal__description--alert' : ''
                  }`}>
                  {isSuccessRematch
                    ? '已為您配對新的專屬顧問，後續將由新顧問為您服務。'
                    : isChangingRematch
                    ? `「${officeName}營業所」目前無合適人選，如欲更換請選擇其他營業所。`
                    : '確認更換後，將由同營業所的其他銷售顧問繼續為您提供服務。'}
                </p>
                {isSuccessRematch && (rematchSalesName || officeName) && (
                  <div className="rematch-modal__info">
                    {rematchSalesName && (
                      <p className="rematch-modal__info-row">
                        <span className="rematch-modal__info-label">
                          專屬顧問
                        </span>
                        <span className="rematch-modal__info-value">
                          {rematchSalesName}
                        </span>
                      </p>
                    )}
                    {officeName && (
                      <p className="rematch-modal__info-row">
                        <span className="rematch-modal__info-label">
                          營業所
                        </span>
                        <span className="rematch-modal__info-value">
                          {officeName}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="rematch-modal__actions">
              {isSuccessRematch ? (
                <button
                  className="rematch-modal__confirm-btn"
                  type="button"
                  onClick={handleCloseModal}>
                  確定
                </button>
              ) : isChangingRematch ? (
                <button
                  className="rematch-modal__change-btn"
                  type="button"
                  disabled={isRematching}
                  onClick={handleForceRematch}>
                  確定更換
                </button>
              ) : (
                <button
                  className="rematch-modal__confirm-btn"
                  type="button"
                  disabled={isRematching}
                  onClick={handleConfirmRematch}>
                  確定配對
                </button>
              )}
              {!isSuccessRematch && (
                <button
                  className="rematch-modal__cancel-btn"
                  type="button"
                  onClick={handleCloseModal}>
                  取消
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Chatroom;
